import {
  adaptAccountReadDetail,
  adaptAccountReadUnreadList,
} from "../accountReadAdapter";
import type {
  AccountReadDetailDto,
  AccountReadFeedbackPayload,
  AccountReadReport,
  AccountReadUnreadSummary,
  AccountReadWatchOption,
  AccountReadWatchSubmitResult,
} from "../accountReadTypes";
import type {
  FinancialConnection,
  FinancialNetWorth,
  FinancialSpendingCategories,
} from "@features/api/memberApi";

// ==================== 1. 银行侧 (Banking Side) DTO ====================

export const ACCOUNT_READ_BANKING_FULL_DTO: AccountReadDetailDto = {
  report_id: "ar_9f3c2a1b8d7e4f60a1b2c3d4e5f6a7b8",
  report_type: "ACCOUNT_READ",
  side: "BANKING",
  headline: "Your income swings. Your spending doesn't.",
  summary_text: [
    "908 transactions, six months. What comes in swings hard — anywhere from $54,200 to $151,900 a month. What goes out barely moves. Two of the five full months I can count ended negative, and about a third of everything that leaves is fixed: rent, loans, insurance, property tax.",
  ],
  content: {
    general_read: {
      coverage: {
        summary:
          "Chase, 6 accounts, Mar 1 – Aug 31, five full months, 908 posted transactions. Your auto loan and one duplicate account are not counted. Last 3 days are still clearing and not counted in monthly totals.",
      },
      net_worth: {
        summary:
          "Your net worth is $342,180, up $157,000 over the last six months. Includes $20,500 you added manually — not shown in the trend.",
      },
      monthly_cashflow: {
        summary:
          "What comes in swings hard — anywhere from $5,400 to $151,900 a month. What goes out barely moves: $5,050 to $5,230 a month. Internal transfers between your own accounts are excluded.",
        months: [
          { month: "Mar", money_in: "85200", money_out: "63300" },
          { month: "Apr", money_in: "54200", money_out: "117500" },
          { month: "May", money_in: "151900", money_out: "108800" },
          { month: "Jun", money_in: "73100", money_out: "108200" },
          { month: "Jul", money_in: "82400", money_out: "63100" },
        ],
        footnote:
          "Figures above each pair are the month's net, after transfers between your own accounts.",
      },
      spending_categories: {
        summary:
          "Housing is your largest category at $1,800 (35.4%) this month. Bank fees are $15 — small, but they never stop.",
      },
    },
    subscriptions: {
      title: "11 subscriptions are quietly costing you",
      subscription_count: 11,
      annualized_total: "1302.00",
      summary:
        "The other eight are steady, single-purpose, and priced the same as when they started. No flag is the good news.",
      price_increases: [
        {
          subject: "Netflix",
          previous_amount: "15.49",
          current_amount: "17.99",
          change_pct: "+16.1%",
          change_date: "2026-08-12",
          summary:
            "Raised from $15.49 to $17.99 on Aug 12, after six months at the old price.",
        },
        {
          subject: "Spotify Premium",
          previous_amount: "10.99",
          current_amount: "11.99",
          change_pct: "+9.1%",
          change_date: "2026-06-01",
          summary:
            "Updated from $10.99 to $11.99 following national pricing adjustments.",
        },
      ],
      capability_overlaps: [
        {
          label: "MUSIC",
          title: "Both include music",
          subjects: ["Spotify", "Apple Music"],
          monthly_total: "19.98",
          summary:
            "You are paying twice for the same thing. What sits inside a bundle never shows on the bill, so the overlap is easy to miss.",
        },
        {
          label: "CLOUD STORAGE",
          subjects: ["iCloud", "Google One"],
          monthly_total: "12.98",
          summary:
            "iCloud and Google One both provide personal cloud backup — $12.98 monthly combined.",
        },
      ],
      highest_totals: [
        {
          subject: "Recharge.city",
          periods: 7,
          total: "19.99",
          since_date: "Feb",
          summary:
            "Charged 7 times since March, $139.93 in total. Whether you still use it, money can't tell.",
        },
        {
          subject: "Equinox Fitness",
          periods: 6,
          total: "1680.00",
          summary:
            "Charged 6 monthly cycles since March, $1,680.00 in total gym memberships.",
        },
      ],
      watch_option: {
        key: "watch_subscriptions",
        label: "Your subscriptions",
        sub: "No quiet rises, no double-ups",
        watch_kind: "self_running",
        active: false,
      },
    },
    idle_cash: {
      title: "Your savings account paid you 76 cents.",
      currency: "USD",
      inflow_total: "493000.00",
      interest_income_total: "0.76",
      inflow_per_interest_unit: "648684.00",
      summary:
        "Eleven cents a month. Over the same seven months, five wires of $75,000 landed in this account. I'm not putting a number on what it could have earned instead — that would take a benchmark, and picking one is the same as recommending a product.",
      watch_option: {
        key: "watch_idle_cash",
        label: "Put your idle cash to work",
        sub: "Every deposit gets checked",
        watch_kind: "needs_chat",
        active: false,
      },
    },
    runway: {
      title: "You could cover 4.2 months without a dollar coming in.",
      currency: "USD",
      liquid_balance: "387400.00",
      fixed_monthly_expense: "92000.00",
      runway_months: "4.2",
      summary:
        "Your fixed commitments run about $92,000 a month, and what you hold covers a bit past four of them. Your deposits arrive anywhere from 22 to 48 days apart — so this is the number that decides whether an unusually long gap is a problem or just a wait.",
      watch_option: {
        key: "watch_runway",
        label: "Build an emergency fund",
        sub: "Sized on what you actually owe",
        watch_kind: "needs_chat",
        active: false,
      },
    },
    annual_bills: {
      title: "Two months a year cost you double.",
      currency: "USD",
      normal_monthly_outflow: "5100.00",
      summary:
        "Those are the months that go under the line — not because you overspent, but because the calendar did. Property tax and two premiums each land in one single month. $18,400 of them falls inside the next 90 days.",
      monthly_outflows: [
        {
          month: "2026-03",
          normal_outflow: "5230.00",
          annual_bill_outflow: "43000.00",
        },
        {
          month: "2026-04",
          normal_outflow: "5100.00",
          annual_bill_outflow: "0.00",
        },
        {
          month: "2026-05",
          normal_outflow: "5050.00",
          annual_bill_outflow: "0.00",
        },
        {
          month: "2026-06",
          normal_outflow: "5180.00",
          annual_bill_outflow: "0.00",
        },
        {
          month: "2026-07",
          normal_outflow: "5090.00",
          annual_bill_outflow: "0.00",
        },
        {
          month: "2026-08",
          normal_outflow: "5150.00",
          annual_bill_outflow: "38500.00",
        },
      ],
      watch_option: {
        key: "watch_big_bills",
        label: "Your big bills, ahead of time",
        sub: "Premiums, property tax, the mortgage",
        watch_kind: "self_running",
        active: false,
      },
    },
  },
  published_at: "2026-09-03T10:35:00Z",
};

// ==================== 2. 投资侧 (Invest Side) DTO ====================

export const ACCOUNT_READ_INVEST_DTO: AccountReadDetailDto = {
  report_id: "ar_2b8e4d1f9a6c3e75f0a1b2c3d4e5f6a7",
  report_type: "ACCOUNT_READ",
  side: "INVEST",
  headline: "Half of what you own, you own through funds.",
  summary_text: [
    "27 holdings, $418,206. You're up $61,940 on what I can see. Almost all of it is stocks — and half of that you hold through funds rather than directly, which is why the numbers on your own screen and the numbers here don't match. Your trading over two years cost $54.",
  ],
  content: {
    general_read: {
      coverage: {
        summary:
          "Schwab, 2 accounts. 142 trades over the last 24 months; fund holdings are current through Jun 30.",
      },
      portfolio_value: {
        summary:
          "Your portfolio is worth $342,180. Includes $20,500 you added manually — not shown in the trend.",
      },
      trading_cost: {
        summary: "Your trading over two years cost $54.",
      },
    },
    sell_rule: {
      title: "You act after the move, not before it.",
      period_start: "2024-09-15",
      period_end: "2026-09-15",
      summary:
        "Every sale in this window came after a fall, and three of the five buys came within a week of a run. Both are the same habit — you move once the price has already told you what happened. Nothing here says the calls were wrong. Only that they land in the same spot.",
      up_sold: 5,
      up_chances: 6,
      down_sold: 1,
      down_chances: 4,
      price_series: [
        { date: "2024-09-15", normalized_value: "1.00" },
        { date: "2024-12-15", normalized_value: "1.08" },
        { date: "2025-03-15", normalized_value: "0.97" },
        { date: "2025-06-15", normalized_value: "1.21" },
        { date: "2025-09-15", normalized_value: "1.15" },
        { date: "2025-12-15", normalized_value: "1.34" },
        { date: "2026-03-15", normalized_value: "1.28" },
        { date: "2026-06-15", normalized_value: "1.52" },
        { date: "2026-09-15", normalized_value: "1.61" },
      ],
      actions: [
        {
          action_id: "act_01",
          date: "2024-10-02",
          normalized_value: "1.03",
          side: "BUY",
        },
        {
          action_id: "act_02",
          date: "2024-11-18",
          normalized_value: "1.06",
          side: "BUY",
        },
        {
          action_id: "act_03",
          date: "2025-01-09",
          normalized_value: "1.02",
          side: "SELL",
        },
        {
          action_id: "act_04",
          date: "2025-05-22",
          normalized_value: "1.18",
          side: "BUY",
        },
        {
          action_id: "act_05",
          date: "2025-08-30",
          normalized_value: "1.16",
          side: "SELL",
        },
        {
          action_id: "act_06",
          date: "2025-11-11",
          normalized_value: "1.31",
          side: "BUY",
        },
        {
          action_id: "act_07",
          date: "2026-01-20",
          normalized_value: "1.30",
          side: "SELL",
        },
        {
          action_id: "act_08",
          date: "2026-04-14",
          normalized_value: "1.33",
          side: "BUY",
        },
        {
          action_id: "act_09",
          date: "2026-07-02",
          normalized_value: "1.49",
          side: "BUY",
        },
        {
          action_id: "act_10",
          date: "2026-08-19",
          normalized_value: "1.55",
          side: "SELL",
        },
      ],
      watch_option: {
        key: "watch_sell_rule",
        label: "Selling discipline",
        sub: "Alert before selling positions that haven't hit target",
        watch_kind: "self_running",
        active: false,
      },
    },
    tax_dates: {
      title: "Three of your positions reach one year soon.",
      window_days: 60,
      summary:
        "In the US, how long you held something changes which rate applies to the gain. I don't know your bracket and I won't put a tax number on it — but I do know the dates. Separately: you sold something at a loss in March and bought it back fifteen days later. Worth telling your accountant.",
      dates: [
        {
          position_id: "pos_8f3a11",
          ticker: "PLTR",
          crosses_on: "2026-09-11",
          days_until: 11,
        },
        {
          position_id: "pos_2c97d4",
          ticker: "AMD",
          crosses_on: "2026-09-24",
          days_until: 24,
        },
        {
          position_id: "pos_5e01b8",
          ticker: "MSFT",
          crosses_on: "2026-10-08",
          days_until: 38,
        },
        {
          position_id: "pos_7b2c90",
          ticker: "NVDA",
          crosses_on: "2026-10-22",
          days_until: 52,
        },
      ],
      watch_option: {
        key: "watch_tax_dates",
        label: "Long-term rate countdown",
        sub: "Notify 30 days before holdings cross into long-term capital gains",
        watch_kind: "self_running",
        active: false,
      },
    },
  },
  published_at: "2026-09-15T10:35:00Z",
};

// ==================== 3. 没有维度侧 (Zero-Dimension) DTO ====================

/** 零维度首次态 (FIRST) - 包含 Add more accounts 兜底卡 */
export const ACCOUNT_READ_ZERO_FIRST_DTO: AccountReadDetailDto = {
  report_id: "ar_7d1c5e9a3b8f2061c4d5e6f7a8b9c0d1",
  report_type: "ACCOUNT_READ",
  side: "INVEST",
  headline: "Half of what you own, you own through funds.",
  summary_text: [
    "27 holdings, $418,206. You're up $61,940 on what I can see. Almost all of it is stocks — and half of that you hold through funds rather than directly, which is why the numbers on your own screen and the numbers here don't match. Your trading over two years cost $54.",
  ],
  content: {
    general_read: {
      coverage: {
        summary:
          "Schwab, 2 accounts. 142 trades over the last 24 months; fund holdings are current through Jun 30.",
      },
      portfolio_value: {
        summary:
          "Your portfolio is worth $342,180. Includes $20,500 you added manually — not shown in the trend.",
      },
      trading_cost: {
        summary: "Your trading over two years cost $54.",
      },
    },
  },
  published_at: "2026-09-15T10:35:00Z",
};

// ==================== 4. 归一化领域模型常量 ====================

export const MOCK_ACCOUNT_READ_BANKING: AccountReadReport =
  adaptAccountReadDetail(ACCOUNT_READ_BANKING_FULL_DTO)!;

export const MOCK_ACCOUNT_READ_INVEST: AccountReadReport =
  adaptAccountReadDetail(ACCOUNT_READ_INVEST_DTO)!;

export const MOCK_ACCOUNT_READ_ZERO_FIRST: AccountReadReport =
  adaptAccountReadDetail(ACCOUNT_READ_ZERO_FIRST_DTO)!;

/** 兼容别名：默认零维度为 FIRST 态 */
export const MOCK_ACCOUNT_READ_ZERO: AccountReadReport =
  MOCK_ACCOUNT_READ_ZERO_FIRST;

/** 投资侧紧凑变体 */
export const MOCK_ACCOUNT_READ_INVEST_COMPACT: AccountReadReport = {
  ...MOCK_ACCOUNT_READ_INVEST,
  reportId: "ar_invest_compact",
};

// ==================== 5. 覆盖卡与图表 Mock 数据源 (对齐 Figma 稿) ====================

export const MOCK_BANKING_CONNECTIONS: FinancialConnection[] = [
  {
    connection_id: "conn_chase_001",
    institution_name: "Chase",
    institution_id: "ins_chase",
    accounts: [
      {
        id: "acc_chase_chk",
        account_name: "Checking",
        account_mask: "1234",
        type: "DEPOSITORY",
      },
      {
        id: "acc_chase_sav",
        account_name: "Checking",
        account_mask: "1234",
        type: "DEPOSITORY",
      },
      {
        id: "acc_chase_card",
        account_name: "Checking",
        account_mask: "1234",
        type: "DEPOSITORY",
      },
    ],
  },
  {
    connection_id: "conn_bofa_002",
    institution_name: "Chase",
    institution_id: "ins_bofa",
    accounts: [
      {
        id: "acc_bofa_adv",
        account_name: "Checking",
        account_mask: "1234",
        type: "DEPOSITORY",
      },
      {
        id: "acc_bofa_sav",
        account_name: "Checking",
        account_mask: "1234",
        type: "DEPOSITORY",
      },
      {
        id: "acc_bofa_card",
        account_name: "Checking",
        account_mask: "1234",
        type: "DEPOSITORY",
      },
    ],
  },
];

export const MOCK_INVEST_CONNECTIONS: FinancialConnection[] = [
  {
    connection_id: "conn_schwab_001",
    institution_name: "Schwab",
    institution_id: "ins_schwab",
    accounts: [
      {
        id: "acc_schwab_brok",
        account_name: "Brokerage",
        account_mask: "1234",
        type: "INVESTMENT",
      },
      {
        id: "acc_schwab_roth",
        account_name: "Roth IRA",
        account_mask: "8891",
        type: "INVESTMENT",
      },
    ],
  },
];

export const MOCK_ZERO_CONNECTIONS: FinancialConnection[] = [
  {
    connection_id: "conn_schwab_zero",
    institution_name: "Schwab",
    institution_id: "ins_schwab",
    accounts: [
      {
        id: "acc_schwab_zero_brok",
        account_name: "Brokerage",
        account_mask: "1234",
        type: "INVESTMENT",
      },
      {
        id: "acc_schwab_zero_roth",
        account_name: "Roth IRA",
        account_mask: "8891",
        type: "INVESTMENT",
      },
    ],
  },
];

export const MOCK_BANKING_NET_WORTH: FinancialNetWorth = {
  amount: "342180.00",
  previous_amount: "185180.00",
  sparkline: [
    { timestamp: 1767225600000, price: 185180 },
    { timestamp: 1772323200000, price: 220000 },
    { timestamp: 1777593600000, price: 205000 },
    { timestamp: 1782864000000, price: 250000 },
    { timestamp: 1788134400000, price: 342180 },
  ],
};

export const MOCK_INVEST_NET_WORTH: FinancialNetWorth = {
  amount: "342180.00",
  previous_amount: "185180.00",
  sparkline: [
    { timestamp: 1767225600000, price: 185180 },
    { timestamp: 1772323200000, price: 220000 },
    { timestamp: 1777593600000, price: 205000 },
    { timestamp: 1782864000000, price: 250000 },
    { timestamp: 1788134400000, price: 342180 },
  ],
};

export const MOCK_ZERO_NET_WORTH: FinancialNetWorth = {
  amount: "342180.00",
  previous_amount: "185180.00",
  sparkline: [
    { timestamp: 1767225600000, price: 185180 },
    { timestamp: 1772323200000, price: 220000 },
    { timestamp: 1777593600000, price: 205000 },
    { timestamp: 1782864000000, price: 250000 },
    { timestamp: 1788134400000, price: 342180 },
  ],
};

export type MockAccountReadScopeRow = { label: string; value: string };

export const MOCK_ACCOUNT_READ_COVERAGE = {
  BANKING: {
    connections: MOCK_BANKING_CONNECTIONS,
    scopeRows: [
      { label: "Range", value: "Feb 20 – Aug 24 · 185 days" },
      { label: "Full months I count", value: "Mar – Jul" },
      { label: "Transactions", value: "908" },
    ],
  },
  INVEST: {
    connections: MOCK_INVEST_CONNECTIONS,
    scopeRows: [
      { label: "Trades", value: "142 · last 24 months" },
      { label: "Before Sep 2024", value: "I can't see what you paid" },
      { label: "Fund holdings as of", value: "Jun 30" },
    ],
  },
} satisfies Record<
  "BANKING" | "INVEST",
  { connections: FinancialConnection[]; scopeRows: MockAccountReadScopeRow[] }
>;

export const MOCK_BANKING_CATEGORIES: FinancialSpendingCategories = {
  categories: [
    { name: "Rent & utilities", amount: "2100.00" },
    { name: "Shopping", amount: "1150.00" },
    { name: "Food & dining", amount: "980.00" },
    { name: "Transportation", amount: "620.00" },
    { name: "Entertainment", amount: "430.00" },
    { name: "Home", amount: "360.00" },
  ],
};

// ==================== 6. 默认未读列表与详情索引表 ====================

export const MOCK_ACCOUNT_READ_UNREAD_LIST: AccountReadUnreadSummary[] =
  adaptAccountReadUnreadList([
    ACCOUNT_READ_BANKING_FULL_DTO,
    ACCOUNT_READ_INVEST_DTO,
  ]);

export const MOCK_ACCOUNT_READ_DETAILS: Record<string, AccountReadReport> = {
  [MOCK_ACCOUNT_READ_BANKING.reportId]: MOCK_ACCOUNT_READ_BANKING,
  [MOCK_ACCOUNT_READ_INVEST.reportId]: MOCK_ACCOUNT_READ_INVEST,
  [MOCK_ACCOUNT_READ_ZERO_FIRST.reportId]: MOCK_ACCOUNT_READ_ZERO_FIRST,
  [MOCK_ACCOUNT_READ_INVEST_COMPACT.reportId]: MOCK_ACCOUNT_READ_INVEST_COMPACT,

  // 别名索引支持
  ar_banking_001: MOCK_ACCOUNT_READ_BANKING,
  ar_invest_002: MOCK_ACCOUNT_READ_INVEST,
  ar_invest_compact: MOCK_ACCOUNT_READ_INVEST_COMPACT,
  ar_zero_003: MOCK_ACCOUNT_READ_ZERO_FIRST,
  ar_zero_first: MOCK_ACCOUNT_READ_ZERO_FIRST,
};

// ==================== 7. 集中内存状态操作 ====================

const readReportIds = new Set<string>();
const feedbackStore = new Map<string, AccountReadFeedbackPayload>();
const reactionStore = new Map<string, "LIKE" | null>();

export const mockStore = {
  markAsRead: (reportId: string) => readReportIds.add(reportId),
  resetReadStatus: () => readReportIds.clear(),
  isRead: (reportId: string) => readReportIds.has(reportId),

  getUnreadRawDtos: () =>
    [ACCOUNT_READ_BANKING_FULL_DTO, ACCOUNT_READ_INVEST_DTO].filter(
      (dto) => !readReportIds.has(String(dto.report_id ?? "")),
    ),

  saveFeedback: (reportId: string, payload: AccountReadFeedbackPayload) =>
    feedbackStore.set(reportId, payload),
  getFeedback: (reportId: string) => feedbackStore.get(reportId),

  saveReaction: (reportId: string, reaction: "LIKE" | null) =>
    reactionStore.set(reportId, reaction),
  getReaction: (reportId: string) => reactionStore.get(reportId) ?? null,

  getReportOptions: (reportId: string): AccountReadWatchOption[] => {
    const report = MOCK_ACCOUNT_READ_DETAILS[reportId];
    if (!report) {
      return [];
    }
    return report.dimensions
      .map((d) => d.watchOption)
      .filter((opt): opt is AccountReadWatchOption => Boolean(opt));
  },

  submitWatchMutation: (
    reportId: string,
    optionKeys: string[],
  ): AccountReadWatchSubmitResult => {
    const available = mockStore.getReportOptions(reportId);
    const keySet = new Set(optionKeys);
    const selected = available.filter((o) => keySet.has(o.key));

    const activated: AccountReadWatchOption[] = [];
    const needsChat: AccountReadWatchOption[] = [];

    for (const opt of selected) {
      if (opt.watchKind === "needs_chat") {
        needsChat.push({ ...opt, active: true });
      } else {
        activated.push({
          ...opt,
          active: true,
          trackerId: opt.trackerId ?? `tr_${opt.key}`,
        });
      }
    }

    return {
      activated,
      needsChat,
      surface:
        activated.length > 0
          ? `Watching ${activated.length} trackers`
          : "Watch created",
    };
  },
};
